import React from 'react';
import { useTranslation } from 'react-i18next';
import { useInvoiceTemplate } from '../../../hooks/useTemplateSettings';
import { useCompanyStore } from '../../../stores/companyStore';
import { InvoiceData } from '../types';
import { InvoiceTemplateClassic } from './InvoiceTemplateClassic';
import { InvoiceTemplateModern } from './InvoiceTemplateModern';
import { InvoiceTemplateCorporate } from './InvoiceTemplateCorporate';
import { InvoiceTemplateElegant } from './InvoiceTemplateElegant';
import { logger } from '../../../utils/logger';

interface InvoiceTemplateRouterProps {
  invoiceData: InvoiceData;
  onClose: () => void;
}

/**
 * Router de templates de factures
 * Sélectionne le bon template selon le nom du template en base de données
 */
export const InvoiceTemplateRouter: React.FC<InvoiceTemplateRouterProps> = ({
  invoiceData,
  onClose
}) => {
  const { t } = useTranslation();
  const { template, loading, error } = useInvoiceTemplate();
  const { company } = useCompanyStore();

  // Chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
            <p className="text-gray-700">{t('templates.common.loadingTemplate')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Erreur
  if (error) {
    logger.error('Erreur chargement template:', error);
    // Fallback sur template classique en cas d'erreur (sans couleur custom)
    return (
      <InvoiceTemplateClassic
        invoiceData={invoiceData}
        onClose={onClose}
        template={template ?? undefined}
        company={company}
        color="#0D9488"
      />
    );
  }

  // Sélectionner le template selon le displayName (pas le name technique)
  // Mapping: displayName (Classic, Modern, Corporate, Elegant) → lowercase
  const templateName = template?.displayName?.toLowerCase() || 'classic';

  // ✅ LOGIC: Utiliser les couleurs de la société SI useCompanyColors = true
  const companyColors = (template as any)?.companyColors;
  const shouldUseCompanyColors = companyColors?.useCompanyColors === true;
  const primaryColor = shouldUseCompanyColors && companyColors?.colorPrimary
    ? companyColors.colorPrimary
    : template?.styleConfig?.primaryColor ?? '#0D9488';

  logger.log('Résolution template facture:', {
    dbName: template?.name,
    displayName: template?.displayName,
    templateName,
    hasStyleConfig: !!template?.styleConfig,
    useCompanyColors: shouldUseCompanyColors,
    colorPrimary: primaryColor,
  });

  switch (templateName) {
    case 'classic':
      return (
        <InvoiceTemplateClassic
          invoiceData={invoiceData}
          onClose={onClose}
          template={template ?? undefined}
          company={company}
          color={primaryColor}
        />
      );

    case 'modern':
      return (
        <InvoiceTemplateModern
          invoiceData={invoiceData}
          onClose={onClose}
          template={template ?? undefined}
          company={company}
          color={primaryColor}
        />
      );

    case 'corporate':
      return (
        <InvoiceTemplateCorporate
          invoiceData={invoiceData}
          onClose={onClose}
          template={template ?? undefined}
          company={company}
          color={primaryColor}
        />
      );

    case 'elegant':
      return (
        <InvoiceTemplateElegant
          invoiceData={invoiceData}
          onClose={onClose}
          template={template ?? undefined}
          company={company}
          color={primaryColor}
        />
      );

    default:
      // Fallback vers template classique pour tout template inconnu
      logger.warn(`Template inconnu: ${templateName}, fallback vers template classique`);
      return (
        <InvoiceTemplateClassic
          invoiceData={invoiceData}
          onClose={onClose}
          template={template ?? undefined}
          company={company}
          color={primaryColor}
        />
      );
  }
};
